/**
 *
 * Copyright 2020-2025 Splunk Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
import { AlwaysOffSampler, AlwaysOnSampler } from '@opentelemetry/sdk-trace-web';
import { getGlobal } from './global-utils';
export class SessionBasedSampler {
    currentSessionId;
    currentSessionSampled;
    notSampled;
    ratio;
    sampled;
    upperBound;
    constructor({ notSampled = new AlwaysOffSampler(), ratio = 1, sampled = new AlwaysOnSampler(), } = {}) {
        this.ratio = this._normalize(ratio);
        // eslint-disable-next-line unicorn/number-literal-case
        this.upperBound = Math.floor(this.ratio * 0xff_ff_ff_ff);
        this.sampled = sampled;
        this.notSampled = notSampled;
    }
    shouldSample(context, traceId, spanName, spanKind, attributes, links) {
        // Implementation based on @opentelemetry/core TraceIdRatioBasedSampler
        // but replacing deciding based on traceId with sessionId
        // (not extended from due to private methods)
        const SplunkRum = getGlobal();
        if (!SplunkRum.sessionManager) {
            throw new Error('SplunkRum is not initialized');
        }
        const currentSessionId = SplunkRum.sessionManager.getSessionId();
        if (this.currentSessionId !== currentSessionId) {
            this.currentSessionSampled = this._accumulate(currentSessionId) < this.upperBound;
            this.currentSessionId = currentSessionId;
        }
        const sampler = this.currentSessionSampled ? this.sampled : this.notSampled;
        return sampler.shouldSample(context, traceId, spanName, spanKind, attributes, links);
    }
    toString() {
        return `SessionBased{ratio=${this.ratio}, sampled=${this.sampled.toString()}, notSampled=${this.notSampled.toString()}}`;
    }
    _accumulate(sessionId) {
        let accumulation = 0;
        for (let i = 0; i < sessionId.length / 8; i++) {
            const pos = i * 8;
            const part = Number.parseInt(sessionId.slice(pos, pos + 8), 16);
            accumulation = (accumulation ^ part) >>> 0;
        }
        return accumulation;
    }
    _normalize(ratio) {
        if (typeof ratio !== 'number' || Number.isNaN(ratio)) {
            return 0;
        }
        return ratio >= 1 ? 1 : Math.max(ratio, 0);
    }
}
//# sourceMappingURL=session-based-sampler.js.map